/**
 * ETQ-01 — Etiquetas de producto para bodega.
 *
 * El contenido lo define una `LabelTemplate` del tenant, no el componente: en un
 * catálogo de repuestos lo que el bodeguero necesita leer es la PROCEDENCIA y el
 * CÓDIGO ORIGINAL, que viven en `Product.attributes` y son dinámicos por
 * categoría. Sin plantilla configurada se cae a `PLANTILLA_BASE` (nombre, código
 * de barras y SKU), que es lo que servía antes.
 *
 * El destino es una impresora de etiquetas, así que la página ES la etiqueta:
 * `@page` toma el tamaño de la plantilla y `columns` dice cuántas entran a lo
 * ancho — 1 = ancho completo, 2 = el ancho dividido en dos.
 *
 * ⚠️ JsBarcode viene del CDN (`index.html` y la ventana de impresión). Está en
 * la allowlist de la CSP, pero sin internet no hay etiquetas — y una bodega es
 * justo donde eso pasa. Bajarlo a dependencia local es trabajo aparte.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Printer, Tag, Columns2, Square } from 'lucide-react'
import { Modal } from '@/components/ds/Modal'
import {
  useLabelTemplates, plantillaPorDefecto, PLANTILLA_BASE,
  type LabelTemplate, type LabelField,
} from '../hooks/useLabels'

declare global {
  interface Window { JsBarcode: any }
}

export interface ProductoEtiquetable {
  name: string
  sku: string
  barcode?: string | null
  barcode_type?: string
  unit_price?: string
  unit?: string
  unit_display?: string
  category_name?: string | null
  description?: string
  attributes?: Record<string, unknown>
}

interface Props {
  open: boolean
  onClose: () => void
  product: ProductoEtiquetable
  /** Precargada al imprimir tras un ingreso a bodega: una etiqueta por unidad. */
  cantidadInicial?: number
}

const escapar = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))

/** Resuelve el texto de una línea de la plantilla contra el producto. */
function valorDelCampo(p: ProductoEtiquetable, key: string): string {
  if (key.startsWith('attr:')) {
    const v = (p.attributes ?? {})[key.slice(5)]
    // Los atributos de repuestos traen listas (aplicaciones, equivalencias): en
    // una etiqueta no cabe la lista entera, así que va la primera.
    if (Array.isArray(v)) return v.length ? String(v[0]) : ''
    return v == null ? '' : String(v)
  }
  switch (key) {
    case 'name': return p.name ?? ''
    case 'sku': return p.sku ?? ''
    case 'price': return p.unit_price ? '$' + parseFloat(p.unit_price).toLocaleString('es-CL') : ''
    case 'unit': return p.unit_display || p.unit || ''
    case 'category': return p.category_name || ''
    case 'description': return p.description || ''
    default: return ''
  }
}

export default function BarcodeLabelPrint({ open, onClose, product, cantidadInicial }: Props) {
  const { data } = useLabelTemplates(open)
  const plantillaTenant = plantillaPorDefecto(data?.templates)

  const [columnas, setColumnas] = useState<1 | 2>(1)
  const [cantidad, setCantidad] = useState(cantidadInicial && cantidadInicial > 0 ? cantidadInicial : 1)
  const barcodeRef = useRef<SVGSVGElement>(null)

  // La plantilla manda; `columnas` es el único ajuste del momento de imprimir
  // (el mismo rollo se usa en los dos formatos según lo que se esté etiquetando).
  const plantilla: Omit<LabelTemplate, 'id' | 'name' | 'is_default' | 'is_active'> = useMemo(() => {
    const base = plantillaTenant ?? PLANTILLA_BASE
    return { ...base, columns: columnas } as typeof base & { columns: number }
  }, [plantillaTenant, columnas])

  useEffect(() => {
    if (plantillaTenant) setColumnas(plantillaTenant.columns === 2 ? 2 : 1)
  }, [plantillaTenant])

  useEffect(() => {
    if (cantidadInicial && cantidadInicial > 0) setCantidad(cantidadInicial)
  }, [cantidadInicial])

  const valorBarras = product.barcode || product.sku
  const formatoBarras = (plantilla.barcode_type === 'ean13' || product.barcode_type === 'ean13')
    ? 'EAN13' : 'CODE128'

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !barcodeRef.current || !window.JsBarcode) return
    const opciones = { width: 2, height: 40, displayValue: true, fontSize: 12, margin: 4 }
    try {
      window.JsBarcode(barcodeRef.current, valorBarras, { format: formatoBarras, ...opciones })
    } catch {
      // EAN13 exige 13 dígitos: un SKU alfanumérico lo rompe y CODE128 lo acepta.
      try { window.JsBarcode(barcodeRef.current, valorBarras, { format: 'CODE128', ...opciones }) } catch { /* no-op */ }
    }
  }, [open, valorBarras, formatoBarras])

  if (!open) return null

  const campos: LabelField[] = plantilla.fields?.length ? plantilla.fields : PLANTILLA_BASE.fields
  const anchoEtiqueta = plantilla.width_mm / (plantilla.columns || 1)

  const lineasHtml = (indice: number) => campos.map((c) => {
    if (c.key === 'barcode') return `<svg class="bc" data-i="${indice}"></svg>`
    const texto = valorDelCampo(product, c.key)
    if (!texto) return ''
    const estilo = [
      `font-size:${c.font_size ?? 10}px`,
      c.bold ? 'font-weight:700' : 'font-weight:400',
      c.muted ? 'color:#666' : 'color:#000',
      'margin:0;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis',
    ].join(';')
    return `<p style="${estilo}">${escapar(texto)}</p>`
  }).join('')

  const handlePrint = () => {
    const v = window.open('', '_blank', 'width=900,height=700')
    if (!v) return

    const etiquetas = Array.from({ length: cantidad }, (_, i) => `
      <div class="etq">${lineasHtml(i)}</div>`).join('')

    v.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Etiquetas ${escapar(product.sku)}</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
<style>
  /* La página ES la etiqueta: el destino es una impresora de etiquetas, no una hoja. */
  @page { size: ${plantilla.width_mm}mm ${plantilla.height_mm}mm; margin: 0; }
  body { margin:0; font-family: Arial, Helvetica, sans-serif; }
  .hoja { display:grid; grid-template-columns: repeat(${plantilla.columns}, 1fr);
          gap:${plantilla.gap_mm}mm; width:${plantilla.width_mm}mm; }
  .etq { box-sizing:border-box; width:${anchoEtiqueta - plantilla.gap_mm}mm;
         height:${plantilla.height_mm}mm; padding:2mm; overflow:hidden;
         display:flex; flex-direction:column; align-items:center; justify-content:center;
         text-align:center; page-break-inside:avoid; }
  .etq svg { max-width:100%; height:auto; }
</style></head><body>
<div class="hoja">${etiquetas}</div>
<script>
  document.querySelectorAll('.bc').forEach(function (el, i) {
    el.id = 'bc-' + i;
    var o = { width: 1.4, height: 32, displayValue: true, fontSize: 9, margin: 2 };
    try { JsBarcode('#bc-' + i, ${JSON.stringify(valorBarras)}, Object.assign({ format: '${formatoBarras}' }, o)); }
    catch (e) { try { JsBarcode('#bc-' + i, ${JSON.stringify(valorBarras)}, Object.assign({ format: 'CODE128' }, o)); } catch (e2) {} }
  });
  setTimeout(function () { window.print(); }, 500);
</script></body></html>`)
    v.document.close()
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><Tag size={18} className="text-primary" /> Imprimir etiquetas</span>} size="md">
      <div className="space-y-4">
        {/* Vista previa: el contenido sale de la plantilla, no del componente */}
        <div className="flex flex-col items-center p-4 bg-glass-3 border border-divider rounded-xl">
          {campos.map((c, i) => {
            if (c.key === 'barcode') return <svg key={i} ref={barcodeRef} />
            const texto = valorDelCampo(product, c.key)
            if (!texto) return null
            return (
              <p key={i} className={`max-w-full truncate ${c.muted ? 'text-content-subtle' : 'text-content'}`}
                 style={{ fontSize: (c.font_size ?? 10) + 'px', fontWeight: c.bold ? 700 : 400 }}>
                {texto}
              </p>
            )
          })}
        </div>

        <p className="text-[11px] text-content-subtle text-center">
          {plantillaTenant
            ? <>Plantilla «{plantillaTenant.name}» · {plantilla.width_mm}×{plantilla.height_mm} mm</>
            : <>Sin plantilla configurada: se usa el formato básico. Se define en Configuración.</>}
        </p>

        {/* Formato */}
        <div>
          <label className="text-xs font-medium text-content-muted">Formato</label>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setColumnas(1)}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                columnas === 1 ? 'bg-primary text-on-primary border-primary' : 'border-divider hover:bg-soft'}`}>
              <Square size={14} /> Ancho completo
            </button>
            <button onClick={() => setColumnas(2)}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                columnas === 2 ? 'bg-primary text-on-primary border-primary' : 'border-divider hover:bg-soft'}`}>
              <Columns2 size={14} /> Dividido en 2
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-content-muted" htmlFor="etq-cantidad">
            Cantidad de etiquetas
          </label>
          <input
            id="etq-cantidad" type="number" min={1} max={500} value={cantidad}
            onChange={(e) => setCantidad(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
            className="w-full mt-1 px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content"
          />
          {cantidadInicial && cantidadInicial > 0 && (
            <p className="mt-1 text-[11px] text-content-subtle">
              Precargada con las {cantidadInicial} unidades que ingresaron.
            </p>
          )}
        </div>

        <button onClick={handlePrint}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover transition-colors">
          <Printer size={16} /> Imprimir {cantidad} {cantidad === 1 ? 'etiqueta' : 'etiquetas'}
        </button>
      </div>
    </Modal>
  )
}
